import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faBars, faTimes, faArrowLeft, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import SJDEFILogo from '../../images/SJDEFILogo.png';
import '../../css/JuanScope/ScopeRegistration1.css';
import SideNavigation from './SideNavigation';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import SessionManager from '../JuanScope/SessionManager';

// Helper function to convert 24-hour time to 12-hour format with AM/PM
const formatTimeTo12Hour = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const adjustedHours = hours % 12 || 12; // Convert 0 to 12 for midnight
  return `${adjustedHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

function ScopeExamInterviewApplication() {
  const navigate = useNavigate();
  const [userData, setUserData] = useState({});
  const [registrationStatus, setRegistrationStatus] = useState('Incomplete');
  const [preferredExamAndInterviewApplicationStatus, setPreferredExamAndInterviewApplicationStatus] = useState('Incomplete');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [nextLocation, setNextLocation] = useState(null);

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [selectedTimeSlotDetails, setSelectedTimeSlotDetails] = useState(null); // Store time slot details
  const [errors, setErrors] = useState({});
  const [availableDates, setAvailableDates] = useState([]);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [showDateConfirmModal, setShowDateConfirmModal] = useState(false);
  const [isDateSaved, setIsDateSaved] = useState(false);
  const [activeTermId, setActiveTermId] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

   const sendSystemLog = async (payload) => {
    if (!payload.action) return;
    try {
      const logResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/system-logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (logResponse.ok) {
        const logResData = await logResponse.json(); // Optional
        console.log('System log recorded:', logResData.message || logResData); // Optional
      } else {
        const errData = await logResponse.json().catch(() => ({ message: 'Failed to parse log error response' })); // Optional
        console.error('Failed to record system log - Server:', logResponse.status, errData.message || errData); // Optional
      }
    } catch (logErr) {
      console.error('Failed to record system log - Network/Fetch:', logErr); // Optional
    }
  };

  const handleLogoutInternal = async () => {
    const applicantIDForLog = localStorage.getItem('applicantID') || 'N/A';
    const firstNameForLog = localStorage.getItem('firstName') || 'User';
    const lastNameForLog = localStorage.getItem('lastName') || '';
    const userEmailForLog = localStorage.getItem('userEmail') || 'Unknown Email'; 

    const accountNameForLog = `${firstNameForLog} ${lastNameForLog}`.trim() || userEmailForLog;

    const logData = {
      userID: applicantIDForLog, 
      accountName: accountNameForLog,
      role: 'Enrollee Applicant', 
      action: 'Logged Out',
      detail: `User ${accountNameForLog} (${applicantIDForLog}) logged out from Scope.`,
    };
    await sendSystemLog(logData);

    try {
      const userEmail = localStorage.getItem('userEmail');
      const createdAt = localStorage.getItem('createdAt');

      if (!userEmail) {
        navigate('/scope-login', { state: { error: 'No active session found.' } });
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/enrollee-applicants/logout`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: userEmail, createdAt }),
        }
      );

      if (response.ok) {
        localStorage.clear();
        navigate('/scope-login');
      } else {
        setError('Failed to log out. Please try again.');
      }
    } catch (err) {
      setError('Error during logout process. Please check your connection.');
    } finally {
      setShowLogoutModal(false);
    }
  };

  useEffect(() => {
    const userEmail = localStorage.getItem('userEmail');
    const createdAt = localStorage.getItem('createdAt');

    if (!userEmail || !createdAt) {
      navigate('/scope-login', { state: { error: 'No active session found. Please log in.' } });
      return;
    }

    const fetchWithRetry = async (url, retries = 3, delay = 1000) => {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: `HTTP error ${response.status}` }));
          throw new Error(errorData.message || `HTTP error ${response.status}`);
        }
        return await response.json();
      } catch (error) {
        if (retries > 0) {
          await new Promise(resolve => setTimeout(resolve, delay));
          return fetchWithRetry(url, retries - 1, delay);
        }
        throw error;
      }
    };

    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        setErrors({});

        const createdAtDate = new Date(createdAt);
        if (isNaN(createdAtDate.getTime())) {
          handleLogoutInternal();
          return;
        }

        const verificationData = await fetchWithRetry(
          `${process.env.REACT_APP_API_URL}/api/enrollee-applicants/verification-status/${userEmail}`
        );

        if (
          verificationData.status !== 'Active' ||
          Math.abs(new Date(verificationData.createdAt).getTime() - createdAtDate.getTime()) > 1000
        ) {
          handleLogoutInternal();
          return;
        }

        const applicantData = await fetchWithRetry(
          `${process.env.REACT_APP_API_URL}/api/enrollee-applicants/personal-details/${userEmail}`
        );
        setUserData({
          email: userEmail,
          firstName: applicantData.firstName || 'User',
          lastName: applicantData.lastName || '',
        });
        setRegistrationStatus(applicantData.registrationStatus || 'Incomplete');

        if (applicantData.registrationStatus !== 'Complete') {
          navigate('/scope-registration-6');
          setLoading(false);
          return;
        }

        let tempSelectedDate = null;
        let tempSelectedTimeSlot = null;
        let tempSelectedTimeSlotDetails = null;
        let tempIsDateSaved = false;
        let termIdFromSavedData = null;
        let termIdFromAvailableDatesApi = null;
        let currentAvailableDates = [];
        let currentUnavailableDates = [];

        try {
          const examData = await fetchWithRetry(
            `${process.env.REACT_APP_API_URL}/api/enrollee-applicants/exam-interview/${userEmail}`
          );
          setPreferredExamAndInterviewApplicationStatus(examData.preferredExamAndInterviewApplicationStatus || 'Incomplete');
if (examData.preferredExamAndInterviewDate) {
  tempSelectedDate = new Date(Date.UTC(
    new Date(examData.preferredExamAndInterviewDate).getUTCFullYear(),
    new Date(examData.preferredExamAndInterviewDate).getUTCMonth(),
    new Date(examData.preferredExamAndInterviewDate).getUTCDate()
  ));
  tempSelectedTimeSlot = examData.selectedTimeSlotId || null;
  tempSelectedTimeSlotDetails = examData.selectedTimeSlot || null;
  tempIsDateSaved = examData.preferredExamAndInterviewApplicationStatus === 'Complete';
  
  // Add this debugging log to verify data is being received:
  console.log('Loaded saved time slot details:', examData.selectedTimeSlot);
}
          if (examData.termId) {
            termIdFromSavedData = examData.termId;
          }
        } catch (err) {
          console.warn('Error fetching existing exam interview data:', err.message);
        }

        try {
          const datesApiResponse = await fetchWithRetry(
            `${process.env.REACT_APP_API_URL}/api/dropdown/exam-dates`
          );
          if (datesApiResponse && datesApiResponse.termId) {
            termIdFromAvailableDatesApi = datesApiResponse.termId;
            if (Array.isArray(datesApiResponse.dates)) {
              currentAvailableDates = datesApiResponse.dates
                .map(item => {
                  if (typeof item.value === 'string' && item.value.match(/^\d{4}-\d{2}-\d{2}$/)) {
                    return new Date(Date.UTC(
                      parseInt(item.value.split('-')[0]),
                      parseInt(item.value.split('-')[1]) - 1,
                      parseInt(item.value.split('-')[2])
                    ));
                  }
                  console.warn('Invalid date format in exam-dates from API:', item.value);
                  return null;
                })
                .filter(date => date instanceof Date && !isNaN(date));
            } else {
              console.warn('Exam dates API response has termId but dates array is missing/invalid:', datesApiResponse);
            }
            const termResponse = await fetchWithRetry(
              `${process.env.REACT_APP_API_URL}/api/admin/terms/${datesApiResponse.termId}`
            );
            if (termResponse && termResponse.data && termResponse.data.dailyExamSchedules) {
              currentUnavailableDates = termResponse.data.dailyExamSchedules
                .filter(schedule => {
                  return schedule.timeSlots.every(slot =>
                    slot.currentCapacity >= slot.maxCapacity || slot.status === 'Unavailable'
                  );
                })
                .map(schedule => new Date(Date.UTC(
                  new Date(schedule.date).getUTCFullYear(),
                  new Date(schedule.date).getUTCMonth(),
                  new Date(schedule.date).getUTCDate()
                )));
            }
          } else {
            console.error('Exam dates API response is missing termId or malformed:', datesApiResponse);
            setError('Failed to load exam date configuration. Calendar might not function correctly.');
          }
        } catch (err) {
          console.error('Error fetching available exam dates:', err.message);
          setError('Failed to load available exam dates. Please check your connection and try again.');
        }

        setSelectedDate(tempSelectedDate);
        setSelectedTimeSlot(tempSelectedTimeSlot);
        setSelectedTimeSlotDetails(tempSelectedTimeSlotDetails);
        setIsDateSaved(tempIsDateSaved);
        setAvailableDates(currentAvailableDates);
        setUnavailableDates(currentUnavailableDates);
        if (tempIsDateSaved && termIdFromSavedData) {
          setActiveTermId(termIdFromSavedData);
        } else if (termIdFromAvailableDatesApi) {
          setActiveTermId(termIdFromAvailableDatesApi);
        } else {
          setActiveTermId(null);
          if (!tempIsDateSaved && currentAvailableDates.length > 0) {
            console.warn("Available dates loaded, but no corresponding term ID was found from the API.");
            setErrors(prev => ({ ...prev, termId: "Configuration error: Missing term ID for available dates. Cannot save." }));
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading data:', err);
        if (err.message.includes('403')) {
          setError('Access denied. Please verify your account.');
        } else if (err.message.includes('429')) {
          setError('Too many requests. Please try again later.');
        } else {
          setError(`Failed to load page data: ${err.message}. Please check connection and try again.`);
        }
        setLoading(false);
      }
    };

    fetchData();
    const refreshInterval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(refreshInterval);
  }, [navigate]);

  useEffect(() => {
  const fetchMissingTimeSlotDetails = async () => {
    if (isDateSaved && selectedTimeSlot && !selectedTimeSlotDetails && activeTermId && selectedDate) {
      try {
        const utcDate = new Date(Date.UTC(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate()
        ));
        const formattedDate = utcDate.toISOString().split('T')[0];
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/dropdown/exam-time-slots?termId=${activeTermId}&date=${formattedDate}`
        );
        if (response.ok) {
          const data = await response.json();
          const timeSlot = data.timeSlots?.find(slot => slot._id === selectedTimeSlot);
          if (timeSlot) {
            setSelectedTimeSlotDetails(timeSlot);
          }
        }
      } catch (err) {
        console.warn('Error fetching missing time slot details:', err);
      }
    }
  };

  fetchMissingTimeSlotDetails();
}, [isDateSaved, selectedTimeSlot, selectedTimeSlotDetails, activeTermId, selectedDate]);

  useEffect(() => {
    const checkAccountStatus = async () => {
      try {
        const userEmail = localStorage.getItem('userEmail');
        const createdAt = localStorage.getItem('createdAt');

        if (!userEmail || !createdAt) {
          return;
        }
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/enrollee-applicants/verification-status/${userEmail}`
        );
        if (!response.ok) throw new Error(`HTTP error ${response.status}`);
        const data = await response.json();

        if (
          data.status !== 'Active' ||
          new Date(data.createdAt).getTime() !== new Date(createdAt).getTime()
        ) {
          handleLogoutInternal();
        }
      } catch (err) {
        console.warn('Error checking account status:', err.message);
      }
    };
    const interval = setInterval(checkAccountStatus, 60 * 1000);
    checkAccountStatus();
    return () => clearInterval(interval);
  }, [navigate]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isFormDirty) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isFormDirty]);

  useEffect(() => {
    if (!selectedDate || isDateSaved) {
      setAvailableTimeSlots([]);
      return;
    }

    const fetchTimeSlots = async () => {
      try {
        const utcDate = new Date(Date.UTC(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate()
        ));
        const formattedDate = utcDate.toISOString().split('T')[0];
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/dropdown/exam-time-slots?termId=${activeTermId}&date=${formattedDate}`
        );
        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }
        const data = await response.json();
        setAvailableTimeSlots(data.timeSlots || []);
        if (data.timeSlots.length === 0 && selectedDate) {
          setErrors(prev => ({ ...prev, timeSlot: 'No available time slots for the selected date. Please choose another date.' }));
        } else {
          setErrors(prev => ({ ...prev, timeSlot: null }));
        }
      } catch (err) {
        console.error('Error fetching time slots:', err);
        setErrors(prev => ({ ...prev, timeSlot: 'Failed to load time slots. Please try again.' }));
        setAvailableTimeSlots([]);
      }
    };

    if (activeTermId && selectedDate) {
      fetchTimeSlots();
    }
  }, [selectedDate, activeTermId, isDateSaved]);

  const handleDateChange = (date) => {
    if (!isDateSaved) {
      setSelectedDate(date);
      setSelectedTimeSlot(null);
      setSelectedTimeSlotDetails(null);
      setErrors(prev => ({ ...prev, selectedDate: null, timeSlot: null }));
      setShowDateConfirmModal(true);
    }
  };

  const handleTimeSlotChange = (e) => {
    const timeSlotId = e.target.value;
    const timeSlot = availableTimeSlots.find(slot => slot._id === timeSlotId);
    setSelectedTimeSlot(timeSlotId);
    setSelectedTimeSlotDetails(timeSlot || null);
    setIsFormDirty(true);
    setErrors(prev => ({ ...prev, timeSlot: null }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!selectedDate) {
      newErrors.selectedDate = 'Selected Date is required';
    } else {
      const selectedDateOnly = new Date(Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate())).getTime();
      const isDateInAvailableList = availableDates.some(d => {
        const availableDateOnly = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).getTime();
        return availableDateOnly === selectedDateOnly;
      });
      if (!isDateInAvailableList) {
        newErrors.selectedDate = 'Selected date is not available';
      }
    }
    if (!selectedTimeSlot && !isDateSaved) {
      newErrors.timeSlot = 'Please select a time slot';
    }
    if (!activeTermId && !isDateSaved) {
      newErrors.termId = 'Cannot save: Academic term not identified. Please refresh or contact support.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirmDate = () => {
    setShowDateConfirmModal(false);
    setIsFormDirty(true);
  };

  const handleCancelDate = () => {
    if (!isDateSaved) {
      setSelectedDate(null);
      setSelectedTimeSlot(null);
      setSelectedTimeSlotDetails(null);
      setIsFormDirty(false);
    }
    setShowDateConfirmModal(false);
    setErrors(prev => ({ ...prev, selectedDate: null, timeSlot: null }));
  };

  const handleNext = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const userEmail = localStorage.getItem('userEmail');
      if (!userEmail) {
        setError('User email not found. Please log in again.');
        navigate('/scope-login');
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/enrollee-applicants/save-exam-interview`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: userEmail,
            termId: activeTermId,
            selectedDate: new Date(Date.UTC(
              selectedDate.getFullYear(),
              selectedDate.getMonth(),
              selectedDate.getDate()
            )).toISOString(),
            selectedTimeSlotId: selectedTimeSlot,
            preferredExamAndInterviewApplicationStatus: 'Complete',
          }),
        }
      );
      const data = await response.json();

      if (response.ok) {
        setIsFormDirty(false);
        setIsDateSaved(true);
        setPreferredExamAndInterviewApplicationStatus('Complete');
        alert(data.message || 'Exam and Interview date and time saved successfully.');
        navigate('/scope-admission-requirements');
      } else {
        setErrors(prev => ({ ...prev, saveError: data.error || `Failed to save (Status: ${response.status})` }));
      }
    } catch (err) {
      console.error('Error saving exam and interview data:', err);
      setErrors(prev => ({ ...prev, saveError: 'An error occurred. Please check connection and try again.' }));
    }
  };

  const handleBack = () => {
    if (isFormDirty) {
      setNextLocation('/scope-registration-6');
      setShowUnsavedModal(true);
    } else {
      navigate('/scope-registration-6');
    }
  };

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const closeSidebar = () => setSidebarOpen(false);

  const handleModalConfirm = () => {
    setShowUnsavedModal(false);
    setIsFormDirty(false);
    if (nextLocation) {
      navigate(nextLocation);
    }
  };

  const handleModalCancel = () => {
    setShowUnsavedModal(false);
    setNextLocation(null);
  };

  return (
    <div className="scope-registration-container">
      <header className="juan-register-header">
        <div className="juan-header-left">
          <img src={SJDEFILogo} alt="SJDEFI Logo" className="juan-logo-register"/>
          <div className="juan-header-text"><h1>JUAN SCOPE</h1></div>
        </div>
        <div className="hamburger-menu">
          <button className="hamburger-button" onClick={toggleSidebar} aria-label="Toggle navigation menu">
            <FontAwesomeIcon icon={sidebarOpen ? faTimes : faBars} size="lg"/>
          </button>
        </div>
      </header>
      <div className="scope-registration-content">
        <SideNavigation userData={userData} onNavigate={closeSidebar} isOpen={sidebarOpen}/>
        <main className={`scope-main-content ${sidebarOpen ? 'sidebar-open' : ''}`}>
          {loading ? (
            <div className="scope-loading">Loading...</div>
          ) : error ? (
            <div className="scope-error">{error}</div>
          ) : (
            <div className="registration-content">
              <h2 className="registration-title">Exam & Interview Application</h2>
              <div className="registration-divider"></div>
              <div className="registration-container">
                <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '1.5rem', textAlign: 'justify' }}>
                  Select your preferred available date and time slot for the exam and interview. Once confirmed, you'll be notified of the scheduled date and time in the Exam & Interview Result.
                </div>
                {(errors.termId || errors.saveError) && (
                  <div className="info-box-warning" style={{ margin: '1rem 0', border: '1px solid #ef9a9a', backgroundColor: '#ffebee', padding: '15px', borderRadius: '4px', color: '#c62828', textAlign: 'left' }}>
                    <p><FontAwesomeIcon icon={faExclamationCircle} style={{ marginRight: '8px' }} />
                      {errors.termId || errors.saveError}
                    </p>
                  </div>
                )}

                {isDateSaved && (
                  <div style={{ margin: '1rem 0', color: '#333', fontSize: '14px', backgroundColor: '#e0f7fa', padding: '1rem', borderRadius: '5px', textAlign: 'left' }}>
                    <p>
                      Your exam and interview date and time have been saved and cannot be changed. Please proceed to view the{' '}
                      <a href="#" onClick={(e) => { e.preventDefault(); navigate('/scope-admission-requirements');}}
                        style={{ color: '#007BFF', textDecoration: 'underline' }}>
                        Admission Requirements
                      </a>.
                    </p>
                  </div>
                )}

                {!isDateSaved && availableDates.length === 0 && !loading && !error && !errors.termId && (
                  <div className="info-box-warning" style={{ margin: '1rem 0', border: '1px solid #ffcc80', backgroundColor: '#fff3e0', padding: '15px', borderRadius: '4px', color: '#bf360c', textAlign: 'left' }}>
                    <p><FontAwesomeIcon icon={faExclamationCircle} style={{ marginRight: '8px' }} />There are currently no available dates for exam and interview scheduling. Please check back later or contact the admissions office for more information.</p>
                  </div>
                )}

                <div className="personal-info-section">
                  <div className="personal-info-header">
                    <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#212121', marginRight: '8px' }}/>
                    <h3>Preferred Exam and Interview Date & Time</h3>
                  </div>
                  <div className="personal-info-divider"></div>
                  <div className="reminder-box" style={{textAlign: 'left'}}>
                    <p>
                      <strong>Reminder:</strong> Please select an available date and time slot. Fields marked with asterisk (<span className="required-asterisk">*</span>) are required. Once saved, the selection cannot be changed.
                    </p>
                  </div>
                  <form onSubmit={e => e.preventDefault()}>
                    <div className="form-grid">
                      <div className="form-group">
                        <label htmlFor="selectedDate">
                          Selected Date:<span className="required-asterisk">*</span>
                        </label>
                        <input
                          type="text"
                          id="selectedDate"
                          name="selectedDate"
                          value={selectedDate
                            ? new Date(Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate())).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                timeZone: 'UTC',
                              })
                            : 'No date selected'}
                          readOnly
                          className={`disabled-input ${errors.selectedDate ? 'input-error' : ''}`}
                        />
                        {errors.selectedDate && (
                          <span className="error-message">
                            <FontAwesomeIcon icon={faExclamationCircle} /> {errors.selectedDate}
                          </span>
                        )}
                      </div>
                      <div className="form-group">
                        <label htmlFor="selectedTimeSlot">
                          Selected Time Slot:<span className="required-asterisk">*</span>
                        </label>
                        <input
                          type="text"
                          id="selectedTimeSlot"
                          name="selectedTimeSlot"
// Updated code:
value={
  selectedTimeSlotDetails?.startTime && selectedTimeSlotDetails?.endTime
    ? `${formatTimeTo12Hour(selectedTimeSlotDetails.startTime)} - ${formatTimeTo12Hour(selectedTimeSlotDetails.endTime)}`
    : selectedTimeSlot && isDateSaved
    ? 'Time slot saved (loading details...)'
    : 'No time slot selected'
}
                          readOnly
                          className={`disabled-input ${errors.timeSlot ? 'input-error' : ''}`}
                        />
                        {!isDateSaved && (
                          <select
                            id="timeSlotSelect"
                            name="timeSlotSelect"
                            value={selectedTimeSlot || ''}
                            onChange={handleTimeSlotChange}
                            disabled={!selectedDate || availableTimeSlots.length === 0}
                            className={errors.timeSlot ? 'input-error' : ''}
                          >
                            <option value="">Select a time slot</option>
                            {availableTimeSlots.map(slot => (
                              <option
                                key={slot._id}
                                value={slot._id}
                                disabled={slot.currentCapacity >= slot.maxCapacity || slot.status === 'Unavailable'}
                              >
                                {`${formatTimeTo12Hour(slot.startTime)} - ${formatTimeTo12Hour(slot.endTime)} (${slot.currentCapacity}/${slot.maxCapacity} slots filled)`}
                              </option>
                            ))}
                          </select>
                        )}
                        {errors.timeSlot && (
                          <span className="error-message">
                            <FontAwesomeIcon icon={faExclamationCircle} /> {errors.timeSlot}
                          </span>
                        )}
                      </div>
                      <div className="form-group">
                        <label>Calendar:</label>
                        {(availableDates.length > 0 || isDateSaved) && !loading ? (
                          <div className="modern-datepicker-wrapper">
                            <ModernDatePickerAdapter
                              selectedDate={selectedDate}
                              onDateChange={handleDateChange}
                              availableDates={availableDates}
                              unavailableDates={unavailableDates}
                              disabled={isDateSaved}
                            />
                          </div>
                        ) : (
                          <div style={{padding: '10px', color: '#757575', textAlign: 'center'}}>
                            {!loading && availableDates.length === 0 && !isDateSaved && "No dates currently available."}
                            {loading && "Loading calendar..."}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="form-buttons">
                      <button type="button" className="back-button" onClick={handleBack}>
                        <FontAwesomeIcon icon={faArrowLeft} /> Back
                      </button>
                      <button
                        type="button"
                        className="next-button"
                        onClick={handleNext}
                        disabled={isDateSaved || !selectedDate || !selectedTimeSlot || (availableDates.length === 0 && !isDateSaved) || !isFormDirty}
                        style={{
                          backgroundColor: (isDateSaved || !isFormDirty || !selectedDate || !selectedTimeSlot) ? '#d3d3d3' : '#34A853',
                          cursor: (isDateSaved || !isFormDirty || !selectedDate || !selectedTimeSlot) ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {isFormDirty ? 'Save and Next' : 'Next'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
      {sidebarOpen && <div className="sidebar-overlay active" onClick={toggleSidebar}></div>}
      {showLogoutModal && (
        <div className="scope-modal-overlay">
          <div className="scope-confirm-modal">
            <h3>Confirm Logout</h3>
            <p>Are you sure you want to logout?</p>
            <div className="scope-modal-buttons">
              <button className="scope-modal-cancel" onClick={() => setShowLogoutModal(false)}>Cancel</button>
              <button className="scope-modal-confirm" onClick={handleLogoutInternal}>Logout</button>
            </div>
          </div>
        </div>
      )}
      {showUnsavedModal && (
        <div className="scope-modal-overlay">
          <div className="scope-confirm-modal">
            <h3>Unsaved Changes</h3>
            <p>You have unsaved changes. Do you want to leave without saving?</p>
            <div className="scope-modal-buttons">
              <button className="scope-modal-cancel" onClick={handleModalCancel}>Stay</button>
              <button className="scope-modal-confirm" onClick={handleModalConfirm}>Leave</button>
            </div>
          </div>
        </div>
      )}
      {showDateConfirmModal && (
        <div className="scope-modal-overlay">
          <div className="scope-confirm-modal">
            <h3>Confirm Date Selection</h3>
            <p>
              You have selected{' '}
              <strong>{selectedDate?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' })}</strong>.
              Please select a time slot next. This selection cannot be changed once saved. Are you sure?
            </p>
            <div className="scope-modal-buttons">
              <button className="scope-modal-cancel" onClick={handleCancelDate}>Cancel</button>
              <button className="scope-modal-confirm" onClick={handleConfirmDate}>Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function ModernDatePickerAdapter({ selectedDate, onDateChange, availableDates, unavailableDates, disabled }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [dateError, setDateError] = useState(null);

  useEffect(() => {
    setDateError(null);
  }, [selectedDate, disabled]);

  const handleDateSelection = (date) => {
    if (disabled) return;

    const selectedDayOnly = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const todayUTC = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));

    if (selectedDayOnly < todayUTC) {
      setDateError('The selected date has already passed. Please choose another available date.');
      return;
    }

    const isAvailable = availableDates.some(d => {
      const availableDayOnly = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
      return availableDayOnly.getTime() === selectedDayOnly.getTime();
    });

    if (!isAvailable) {
      setDateError('Selected date is not available or fully booked. Please choose from the highlighted dates.');
      return;
    }

    setDateError(null);
    onDateChange(selectedDayOnly);
  };

  const validAvailableDates = Array.isArray(availableDates)
    ? availableDates
        .filter(d => d instanceof Date && !isNaN(d.getTime()))
        .map(d => new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())))
    : [];
  const validUnavailableDates = Array.isArray(unavailableDates)
    ? unavailableDates
        .filter(d => d instanceof Date && !isNaN(d.getTime()))
        .map(d => new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())))
    : [];

  return (
    <SessionManager>
      <div className="modern-datepicker">
        {dateError && !disabled && (
          <div className="date-error-message" style={{ color: 'red', marginBottom: '10px', fontSize: '0.9em', textAlign: 'left' }}>
            <FontAwesomeIcon icon={faExclamationCircle} style={{ marginRight: '5px' }} />
            {dateError}
          </div>
        )}
        <DatePicker
          selected={selectedDate}
          onChange={handleDateSelection}
          minDate={today}
          maxDate={validAvailableDates.length > 0
            ? new Date(Math.max(...validAvailableDates.map(d => d.getTime())))
            : (selectedDate || new Date(new Date().setMonth(new Date().getMonth() + 3)))
          }
          includeDates={disabled && selectedDate ? [selectedDate] : (validAvailableDates.length > 0 ? validAvailableDates : undefined)}
          excludeDates={validUnavailableDates}
          inline
          calendarClassName="modern-calendar"
          disabled={disabled}
          dayClassName={(date) => {
            const currentDayOnly = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
            const selectedDayOnlyTime = selectedDate
              ? new Date(Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate())).getTime()
              : null;

            const isSelected = selectedDayOnlyTime === currentDayOnly.getTime();

            if (disabled) {
              return isSelected ? 'selected-date saved-date' : 'unavailable-date';
            }

            const isAvailable = validAvailableDates.some(
              (d) => new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).getTime() === currentDayOnly.getTime()
            );
            const isUnavailable = validUnavailableDates.some(
              (d) => new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).getTime() === currentDayOnly.getTime()
            );
            const isPast = currentDayOnly < today;

            if (isSelected) return 'selected-date';
            if (isAvailable && !isPast && !isUnavailable) return 'available-date';
            if (isPast || isUnavailable) return 'passed-date unavailable-date';
            return 'unavailable-date';
          }}
          popperPlacement="auto"
        />
      </div>
    </SessionManager>
  );
}

export default ScopeExamInterviewApplication;